import { isUuid } from '@/shared/clinical/detailPage'
import { isDayIso } from '@/shared/schedule/day'

// Stored document and laboratory source UUIDs include deterministic IDs without RFC version/variant bits.
const STORED_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Where a Patient chart was opened from, as a CLOSED VOCABULARY rather than a
 * URL.
 *
 * `safeReturnTarget` exists one route over and deliberately is not reused here.
 * It reduces an untrusted string to any safe INTERNAL path, which is the right
 * contract for a login redirect — the visitor was going somewhere and should
 * land there. It is the wrong contract for this: a chart's Back control must
 * lead to the schedule context that opened it, and "any internal path" would
 * let a crafted link put a control reading `Back to Today` on something that
 * navigates into an arbitrary clinical deep link. Widening that validator to
 * serve both would weaken the one that guards sign-in.
 *
 * So nothing here is a URL. The origin is one of three named things, and the
 * only free value is a civil day that must parse as one. There is no string to
 * sanitize, no origin to escape and nothing to resolve against a base — a
 * malformed value cannot become a working link, it can only fail to be an
 * origin at all.
 *
 * It carries NO authority. The day is a view context; it is never sent as
 * clinic or Patient scope, and every request the chart makes is scoped by the
 * session cookie exactly as before.
 */
export type PatientOrigin =
  { readonly kind: 'today' } | { readonly kind: 'schedule'; readonly day: string } | { readonly kind: 'registry' }

export const REGISTRY_ORIGIN: PatientOrigin = { kind: 'registry' }

/** The search parameters a Patient chart accepts. Anything else is dropped. */
export interface PatientOriginSearch {
  from?: 'today' | 'schedule'
  date?: string
}

/**
 * The patient card's sections, as a CLOSED VOCABULARY like the origin above.
 *
 * EMR Records is the DEFAULT and is deliberately not a value `tab` can carry:
 * with exactly one spelling per state, two links to the same section cannot
 * disagree about the URL, and `?tab=records` cannot linger as a synonym that
 * later diverges from the bare route.
 */
export type PatientCardTab =
  | 'records'
  | 'cases'
  | 'files'
  | 'title-sheet'
  | 'clinical-summary'
  | 'medical'
  | 'anamnesis'
  | 'schedule'
  | 'tasks'
  | 'stay'
  | 'finance'
  | 'laboratory'
  | 'imaging'
  | 'messages'

/**
 * The laboratory workspace's own sections, under `tab=laboratory`.
 *
 * A SECOND KEY RATHER THAN THREE MORE TABS. Laboratory is one section of the
 * chart that replaces the card's menu with its own while it is open, so which
 * laboratory view is showing is a state INSIDE that section. Spelling them
 * `tab=laboratory-table` would flatten the patient menu and the laboratory menu
 * into one vocabulary, and nothing could then tell "which chart section" from
 * "which laboratory view" without taking the value apart.
 *
 * Graphs is the DEFAULT and, like Records above, is deliberately not a value
 * `lab` can carry: the laboratory landing has exactly one spelling.
 */
export type LaboratorySection = 'graphs' | 'table' | 'files' | 'imports'

/**
 * The Finance workspace's own sections, under `tab=finance`.
 *
 * THE SAME SHAPE AS THE LABORATORY ABOVE, for the same reason. Finance is one
 * section of the chart that replaces the card's menu with its own while it is
 * open. Patient finance now exposes treatment estimates only; account
 * receivables, funds and owner statements live in the Finance workspace.
 */
export type FinanceSection = 'estimates'

/**
 * The spelling of a registered EXTERNAL SUBSYSTEM's module code, mirrored
 * from the backend registry's own validation (`integrations/registry.go`):
 * lowercase, digits and underscores, 2–64 characters. The union above stays
 * CLOSED — an external module is not a new `tab` value but a second key under
 * the one generic `tab=module`, exactly as a laboratory view is a key under
 * `tab=laboratory` — so no vendor name ever enters the Core vocabulary and
 * every existing Core link keeps its exact spelling.
 *
 * The pattern is a SHAPE check, not an existence check: a well-formed code
 * this build does not host still validates, and the section host renders its
 * safe worded state for it. A malformed value is dropped like any unknown
 * key, landing the link on EMR Records.
 */
const MODULE_CODE = /^[a-z][a-z0-9_]{1,63}$/

export function isExternalModuleCode(value: unknown): value is string {
  return typeof value === 'string' && MODULE_CODE.test(value)
}

export interface PatientCardSearch extends PatientOriginSearch {
  /** External entry opened inside the default EMR journal. */
  externalEntry?: string
  tab?: Exclude<PatientCardTab, 'records'> | 'module'
  /**
   * Meaningful only beside `tab=module`: which external subsystem's section
   * is open. A registered module CODE by shape, never a URL or a component
   * name; the validator drops the pair whenever the code is malformed.
   */
  module?: string
  /** Meaningful only beside `tab=laboratory`. The validator drops it elsewhere. */
  lab?: Exclude<LaboratorySection, 'graphs'>
  /** Files in the dedicated recognition workspace, scoped to Laboratory Files. */
  labFiles?: string[]
  /** Exact confirmed report, independent of its recognition draft or PDF. */
  labReport?: string
  /** Meaningful only beside `tab=finance`. The validator drops it elsewhere. */
  fin?: FinanceSection
  /**
   * Meaningful only beside `tab=finance&fin=estimates`: the treatment estimate
   * the builder or the issued detail is open on. Dropped anywhere else, and
   * dropped when it is not a UUID, so a bookmark cannot carry a stale key into
   * a view that has no use for it.
   */
  estimate?: string
  /**
   * Meaningful only beside `tab=files`: the imaging import whose review the
   * section opens on arrival — the Control Center's upload ledger builds it.
   * An id, never a free string, and the validator drops it elsewhere.
   */
  review?: string
  /**
   * Meaningful only beside `tab=files`: the folder the file registry stands
   * in, so the browser's Back climbs out of it the way the registry's own
   * Back does and a link can open a chart inside one folder. An id, never a
   * path; absent is the root.
   */
  folder?: string
}

/**
 * Read an origin out of untrusted search parameters.
 *
 * Every failure lands on the registry rather than on a guess: an unknown
 * `from`, a `schedule` without a day, a day that is not a civil date. Falling
 * back to Patients is always truthful — it is where the chart is reachable from
 * — whereas defaulting to Today would put a control on screen claiming an
 * origin the clinician never came from.
 */
export function patientOriginFrom(search: PatientOriginSearch): PatientOrigin {
  if (search.from === 'today') {
    // No date, on purpose. Today is whatever the SERVER resolves in the
    // clinic's zone when the clinician returns; a date carried from the moment
    // they left would pin a day the browser computed, and be wrong for anyone
    // who crossed midnight or sits in another zone.
    return { kind: 'today' }
  }
  if (search.from === 'schedule' && isDayIso(search.date)) {
    return { kind: 'schedule', day: search.date }
  }
  return REGISTRY_ORIGIN
}

/** The search a Patient link must carry to be returnable to this origin. */
export function patientOriginSearch(origin: PatientOrigin): PatientOriginSearch {
  switch (origin.kind) {
    case 'today':
      return { from: 'today' }
    case 'schedule':
      return { from: 'schedule', date: origin.day }
    case 'registry':
      return {}
  }
}

/**
 * Validate the Patient route's search.
 *
 * Kept narrow deliberately: unknown keys are dropped and a `date` survives only
 * alongside `from=schedule`, so a link cannot smuggle a value the chart would
 * echo back into a URL later. The `tab` survives only as one of the non-default
 * sections — anything else, including a spelled-out `records`, drops to the bare
 * route, which the page reads as EMR Records.
 */
export function validatePatientSearch(search: Record<string, unknown>): PatientCardSearch {
  const from = search['from']
  const date = search['date']
  const validated = validateSection(
    search['tab'],
    search['lab'],
    search['fin'],
    search['review'],
    search['estimate'],
    search['module'],
    search['folder'],
  )
  if (validated.tab === undefined && typeof search['externalEntry'] === 'string' && isUuid(search['externalEntry'])) validated.externalEntry = search['externalEntry']
  if (validated.tab === 'laboratory' && validated.lab === 'files' && Array.isArray(search['labFiles'])) {
    const files = [...new Set(search['labFiles'].filter((id): id is string => typeof id === 'string' && STORED_UUID_RE.test(id)))]
    if (files.length > 0) validated.labFiles = files
  }
  if (validated.tab === 'laboratory' && (validated.lab === 'files' || validated.lab === 'imports') &&
    typeof search['labReport'] === 'string' && STORED_UUID_RE.test(search['labReport'])) {
    validated.labReport = search['labReport']
  }
  if (from === 'today') {
    return { ...validated, from: 'today' }
  }
  if (from === 'schedule' && typeof date === 'string' && isDayIso(date)) {
    return { ...validated, from: 'schedule', date }
  }
  return validated
}

/**
 * The section and its view, decided together rather than one key at a time.
 *
 * A SUBSECTION KEY SURVIVES ONLY BESIDE THE SECTION THAT OWNS IT. A laboratory
 * view is not a place the card can be while the laboratory section is closed,
 * and neither is a finance view — so a `lab` or a `fin` arriving on its own is
 * not a partial state worth preserving. It is a value the page would otherwise
 * carry, and later echo into a URL that addresses nothing. An unknown view
 * drops the same way an unknown tab does, leaving the bare `tab=laboratory` or
 * `tab=finance`, which the page reads as Graphs and as the Finance landing view.
 *
 * The keys cannot survive together, because the sections cannot both be open:
 * whichever section `tab` names is the only one whose own key is read. A
 * `review` beside `tab=files` names one row rather than a view, and it survives
 * only as an id — a link's claim about an object, checked before anything is
 * asked of the server. `folder` beside it is the same kind of claim.
 */
function validateSection(
  tab: unknown,
  lab: unknown,
  fin: unknown,
  review: unknown,
  estimate: unknown,
  module: unknown,
  folder: unknown,
): PatientCardSearch {
  if (tab === 'module') {
    // The generic external-subsystem section. The code survives only as a
    // well-formed module code — the same shape the backend registry enforces
    // — and NEVER alone: `module` without its tab addresses nothing, and
    // `tab=module` without a valid code is a link to nowhere, which drops to
    // the bare route the way every unknown tab does. Whether the code names
    // a module this build hosts is the section host's question, answered
    // with a worded safe state rather than by silently rerouting the link.
    return isExternalModuleCode(module) ? { tab, module } : {}
  }
  if (tab === 'files') {
    return {
      tab,
      ...(typeof review === 'string' && isUuid(review) ? { review } : {}),
      ...(typeof folder === 'string' && isUuid(folder) ? { folder } : {}),
    }
  }
  if (
    tab === 'cases' ||
    tab === 'title-sheet' ||
    tab === 'clinical-summary' ||
    tab === 'medical' ||
    tab === 'anamnesis' ||
    tab === 'schedule' ||
    tab === 'stay' ||
    tab === 'tasks' ||
    tab === 'messages' ||
    tab === 'imaging'
  ) {
    return { tab }
  }
  if (tab === 'laboratory') {
    return lab === 'table' || lab === 'files' || lab === 'imports' ? { tab, lab } : { tab }
  }
  if (tab === 'finance') {
    if (fin === 'estimates') {
      // The one finance view with a row of its own in the URL: the estimate
      // key opens the builder or the issued detail, and only a real UUID
      // beside this exact view survives — see `PatientCardSearch.estimate`.
      return typeof estimate === 'string' && isUuid(estimate) ? { tab, fin, estimate } : { tab, fin }
    }
    return { tab }
  }
  return {}
}
